import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Select from '@components/Form/Select/Select';
import Input from '@components/Form/Input/Input';
import Button from '@components/Button/Button';
import evaluationsService from '@/services/evaluations/evaluationsService';

const ADD_NEW = '__add_new_judge__';

interface Props {
    value: string;
    onChange: (judgeId: string) => void;
    label?: string;
    required?: boolean;
    isDisabled?: boolean;
    /** Compact = no label, used inline in toolbars. */
    placeholder?: string;
}

/**
 * Judge picker used everywhere a judge needs to be identified (score form,
 * download template, import). The roster is inline create-as-you-go — there's
 * no separate "Judges" admin page, so "+ Add a new judge" is always an option
 * here, and it's safe to add: the backend find-or-creates by name.
 */
const JudgeSelect: React.FC<Props> = ({ value, onChange, label, required, isDisabled, placeholder }) => {
    const { t } = useTranslation('evaluation');
    const queryClient = useQueryClient();
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    const { data: judges = [], isLoading } = useQuery({
        queryKey: ['evaluation-judges'],
        queryFn: () => evaluationsService.listJudges(),
        staleTime: 60_000,
    });

    const createMutation = useMutation({
        mutationFn: () =>
            evaluationsService.createJudge({ name: name.trim(), email: email.trim() || undefined }),
        onSuccess: (judge) => {
            queryClient.invalidateQueries({ queryKey: ['evaluation-judges'] });
            onChange(judge.id);
            setAdding(false);
            setName('');
            setEmail('');
            toast.success(t('judge.added', { name: judge.name }));
        },
        onError: () => toast.error(t('judge.addFailed')),
    });

    const options = useMemo(
        () => [
            ...judges.map((j) => ({ value: j.id, label: j.email ? `${j.name} (${j.email})` : j.name })),
            { value: ADD_NEW, label: t('judge.addNew') },
        ],
        [judges, t],
    );

    return (
        <div>
            <Select
                label={label ?? t('judge.label')}
                placeholder={placeholder ?? t('judge.placeholder')}
                required={required}
                options={options}
                value={value}
                isDisabled={isDisabled || isLoading}
                isClearable={false}
                onChange={(v) => {
                    if (v === ADD_NEW) {
                        setAdding(true);
                        return;
                    }
                    onChange(v || '');
                }}
            />
            {adding && (
                <div className="mt-2 p-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 space-y-2">
                    <Input
                        placeholder={t('judge.namePlaceholder')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                    />
                    <Input
                        placeholder={t('judge.emailPlaceholder')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <div className="flex gap-2 justify-end">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                setAdding(false);
                                setName('');
                                setEmail('');
                            }}
                        >
                            {t('judge.cancel')}
                        </Button>
                        <Button
                            size="sm"
                            variant="primary"
                            disabled={!name.trim() || createMutation.isPending}
                            isLoading={createMutation.isPending}
                            onClick={() => createMutation.mutate()}
                        >
                            {t('judge.add')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JudgeSelect;
